// The render worker's contract (P0.8).
//
// Neutral ground, for the same reason as the worklet's: the main thread spawns the worker and the
// worker never imports back. Both need to agree on what crosses.

/**
 * Frame statistics, written by whichever thread is rendering and read by the UI.
 *
 * A SharedArrayBuffer rather than postMessage, and the reason is the same one that shaped the
 * signal ring: the writer is on a 8.3 ms frame budget (Architecture §6.3), and a message per
 * report is an allocation on that budget. Three atomic stores are not.
 *
 * It also makes the two render paths identical. The worker and the main-thread fallback write the
 * same buffer the same way, which is what "behind the same host interface" has to mean if it is
 * to mean anything — a fallback that reports differently is a fallback nobody trusts.
 *
 * Microseconds in an int32 for the same reason as COMMAND_PROBE: a 32-bit store is atomic and a
 * 64-bit float write is not.
 */
export const RENDER_STATS = {
  /** i32 — frames rendered since start. The UI derives FPS from how fast this climbs. */
  FRAME_COUNT: 0,
  /** i32 — the last frame's render time. */
  LAST_FRAME_MICROS: 4,
  /** i32 — the worst since reset. A mean hides the dropped frame; the max is the whole story. */
  MAX_FRAME_MICROS: 8,
  /**
   * i32 — the longest gap between two frames since reset. The worst stall, unbucketed (P0.12).
   *
   * A scalar as well as a histogram, because the histogram cannot answer this. It tops out at
   * 63.75 ms and everything worse lands in one overflow bucket, so a 100 ms hitch and a 3-second
   * freeze are the same reading — and a bench whose job is spotting regressions would report
   * identical numbers as stalls got ten times worse. Found by the test that drove a 100 ms stall
   * through the loop and got 64 ms back.
   *
   * Widening the buckets was the alternative and it is the wrong trade: it would cost resolution
   * across the 8–22 ms range where NFR-P3 actually lives, to describe a tail whose exact shape
   * nobody acts on. One extra `Atomics.store` per frame buys the number instead.
   */
  MAX_INTERVAL_MICROS: 12,
  /**
   * i32[FRAME_HISTOGRAM.BUCKETS] — how long each frame took to *arrive*, bucketed (P0.12).
   *
   * Its own cache line, away from the three scalars above: those are written every frame and read
   * by a poll, and a histogram sharing their line would drag the whole thing between cores.
   */
  INTERVAL_HISTOGRAM: 64,
  /** i32[FRAME_HISTOGRAM.BUCKETS] — how long each frame took to *submit*, bucketed (P0.12). */
  CPU_HISTOGRAM: 64 + 256 * 4,
} as const;

/**
 * The frame-time distribution (P0.12), parked at P0.8 to be designed here.
 *
 * P0.8 shipped last/max, and said why that would not hold: "RENDER_STATS carries last/max frame
 * micros, which is enough for a readout and not enough for Architecture §6.3's frame-time
 * controller (it needs a distribution to apply hysteresis against). P0.12's bench harness wants the
 * same thing. Worth designing once, there." This is that design.
 *
 * A histogram rather than a ring of samples, for the reason everything on this path is what it is:
 * recording a frame must be O(1), allocation-free, and safe from two threads. `Atomics.add` into a
 * fixed bucket is one instruction. A growable array of samples would allocate on the frame budget
 * — and the budget is 8.3 ms (§6.3), which is not where a GC pause belongs.
 *
 * The cost is resolution: a percentile read from a histogram is accurate to one bucket, never
 * better. 250 µs buckets against a 16.7 ms frame is ~1.5% — far finer than the differences anyone
 * acts on, and the honest way to report it is the bucket's upper edge (see `percentileMicros`).
 *
 * **Two histograms, and the second one is the point.** Interval is how long the frame took to
 * arrive — the reciprocal of FPS, and therefore the only one NFR-P3 is about. CPU is how long our
 * JavaScript spent submitting it. They diverge, and the divergence is the diagnosis: GL calls are
 * asynchronous, so a GPU-bound frame shows a *small* CPU time and a large interval. A bench that
 * measured only CPU time would report a comfortable 2 ms while the user watched 30 FPS.
 */
export const FRAME_HISTOGRAM = {
  /** 255 linear buckets + 1 overflow. 256 keeps each histogram exactly 1 kB and 16-line aligned. */
  BUCKETS: 256,
  /** Bucket i covers [i·250 µs, (i+1)·250 µs). */
  BUCKET_MICROS: 250,
  /** The last bucket is unbounded: everything ≥63.75 ms lands there rather than being lost. */
  OVERFLOW_BUCKET: 255,
} as const;

/**
 * Three scalars on their own cache line, then two 1 kB histograms: 64 + 1024 + 1024.
 *
 * Only the rendering thread writes it, whichever thread that is.
 */
export const RENDER_STATS_BYTES = 64 + FRAME_HISTOGRAM.BUCKETS * 4 * 2;

/** Hand the worker a canvas and somewhere to report. Sent once, with the canvas transferred. */
export interface RenderWorkerInitMessage {
  readonly type: 'init';
  readonly canvas: OffscreenCanvas;
  readonly stats: SharedArrayBuffer;
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
}

/** The canvas belongs to the worker now, so only the worker can resize its drawing buffer. */
export interface RenderWorkerResizeMessage {
  readonly type: 'resize';
  readonly width: number;
  readonly height: number;
  readonly pixelRatio: number;
}

export interface RenderWorkerDisposeMessage {
  readonly type: 'dispose';
}

export type RenderWorkerMessage =
  RenderWorkerInitMessage | RenderWorkerResizeMessage | RenderWorkerDisposeMessage;

/**
 * ⚠️ The one thing a render worker says back, and it exists because a silent failure cost a
 * 30-minute soak (P4.15).
 *
 * The worker's own header said "nothing here posts back — frame stats go through the SAB", which
 * was true of every path that works. On 11 September a Chrome window came up with its GPU process
 * unavailable (`BindToCurrentSequence failed`, `GL_VENDOR = Disabled`), both renderers threw inside
 * their `init` handler, and **the main thread never found out**. `RenderLoop.start()` was never
 * reached, the frame counter stayed at zero for thirty minutes, and the bench report was
 * indistinguishable from one taken on a page that had been minimised — three budgets reading "not
 * evaluated" and nothing anywhere saying why.
 *
 * A worker that cannot start is not an edge case a SAB can express: the SAB carries *frames*, and
 * the whole problem is that there are none. So this is the exception to the no-chatter rule, and it
 * is deliberately the only one.
 */
export interface RenderWorkerStartupFailedEvent {
  readonly type: 'startup-failed';
  /** What was thrown, as a string — an Error does not survive structured cloning with its stack. */
  readonly message: string;
}

export type RenderWorkerEvent = RenderWorkerStartupFailedEvent;

/**
 * Where rendering happens.
 *
 * Architecture §2: off-main via OffscreenCanvas "where available (all Tier 1; modern Tier 2)",
 * with a main-thread fallback behind the same interface. PRD §6.3 is the reason there is a
 * fallback at all — Tier 2 degrades, never crashes.
 */
export type RenderPath = 'worker' | 'main-thread';
